import React, { memo, ReactNode } from 'react';

import HelpIcon from '@mui/icons-material/Help';
import { Box, Button, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip/Tooltip';
import { theme } from '@provider-portal/common/styles/themeMui';
import { $tid } from '@provider-portal/common/utils/testIdConfig';

interface ContainerCardProps {
  title?: string;
  description?: string;
  contentClassName?: string;
  width?: string;
  toolTipText?: string;
  testId?: string;
  actionLabel?: string;
  actionMinWidth?: string;
  onActionHandler?: () => void;
  actionButton?: ReactNode;
}

const HelpTooltip: React.FC<{
  toolTipText: string;
  sx?: TooltipProps['sx'];
}> = (props) => (
  <Tooltip
    sx={props.sx}
    enterTouchDelay={0}
    leaveTouchDelay={4000}
    title={
      <Typography variant="inherit" data-testid={$tid.components.ContainerCard.TooltipView.Tooltip}>
        {props.toolTipText}
      </Typography>
    }
    arrow
    placement="bottom-end"
    data-testid={$tid.components.ContainerCard.TooltipView.Icon}
  >
    <HelpIcon />
  </Tooltip>
);

export const ContainerCard: React.FC<ContainerCardProps> = memo((props) => {
  const hasAction = (props.actionLabel && props.onActionHandler) || props.actionButton;
  return (
    <Card
      variant="outlined"
      sx={props.width ? { width: props.width } : {}}
      data-testid={props.testId}
    >
      {props.title ? (
        <Box p={2}>
          <Grid container direction="row">
            <Grid
              item
              xs="auto"
              sx={{
                maxWidth:
                  // There is room for improvement here!
                  hasAction && props.actionMinWidth
                    ? `calc(100% - ${props.actionMinWidth})`
                    : '100%',
              }}
            >
              <Typography variant="subtitleL" data-testid={$tid.components.ContainerCard.Title}>
                {props.title}
              </Typography>
              {props.toolTipText && hasAction && (
                <HelpTooltip
                  toolTipText={props.toolTipText}
                  sx={{
                    verticalAlign: 'bottom',
                    marginLeft: theme.spacing(1),
                  }}
                />
              )}
            </Grid>

            {!hasAction && props.toolTipText && (
              <Grid container item xs pl={1} justifyContent="flex-end" alignItems="flex-start">
                <HelpTooltip toolTipText={props.toolTipText} />
              </Grid>
            )}

            {hasAction && (
              <Grid container item xs justifyContent="flex-end" alignItems="flex-start">
                {!!props.actionLabel && !!props.onActionHandler && (
                  <Button type="button" variant="text" size="small" onClick={props.onActionHandler}>
                    {props.actionLabel}
                  </Button>
                )}
                {!!props.actionButton && props.actionButton}
              </Grid>
            )}
          </Grid>

          {props.description ? (
            <Grid container direction="row" pt="8px">
              <Grid item xs="auto" sx={{ flex: 1 }}>
                <Typography variant="bodyM" color="textSecondary">
                  {props.description}
                </Typography>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      ) : null}

      <CardContent data-testid={$tid.components.ContainerCard.Content}>
        {props.children}
      </CardContent>
    </Card>
  );
});
